import React from 'react';
import {
  decodeGppString,
  installTcfCoreApiOnWindow,
  setTcfVendorCap,
} from '@digiwedge/cmp-sdk-web';
import { loadPinnedGvl } from '../../tcf/gvl.js';
import { runConsentModeChecks } from '../../diagnostics/consentMode.js';
import {
  fetchConsentCoverage,
  fetchExperimentsSeries,
  fetchExperimentsSummary,
} from '../../diagnostics/analytics.api.js';
import { useQuery } from '@tanstack/react-query';
// export helpers are used inside dedicated cards
import { useGlobalStore, type GlobalState } from '@digiwedge/global-state';
import { ReceiptsTable } from '../../diagnostics/ReceiptsTable.js';
import { GppTcfHeader } from '../components/GppTcfHeader.js';
import { GcmCard } from '../components/GcmCard.js';
import { RegionDecisionCard } from '../components/RegionDecisionCard.js';
import { ReceiptsExportCard } from '../components/ReceiptsExportCard.js';
import { ConsentsExportCard } from '../components/ConsentsExportCard.js';
import { FirstRunChecklist } from '../components/FirstRunChecklist.js';
// metrics are rendered within MetricsSnapshot; range toolbar moved to components
import CoverageCard from '../components/CoverageCard.js';
import ScansCard from '../components/ScansCard.js';
import ABannerDemo from '../components/ABannerDemo.js';
import MetricsSnapshot from '../components/MetricsSnapshot.js';
import { fetchScansDaily } from '../../diagnostics/scans.api.js';
// charts are lazy-loaded inside child cards
// A/B demo utilities (local, no cross-package dependency)
import { useExperimentVariant } from '@digiwedge/cmp-consent-react';

type Decoded = ReturnType<typeof decodeGppString> | null;
type RegionCardDecision = {
  region: string;
  framework: string;
  status: number;
  latencyMs: number;
  fallback: boolean;
  source: 'network' | 'cache' | 'fallback' | 'static';
};

export default function AdminDiagnostics() {
  const [gpp, setGpp] = React.useState<string | null>(null);
  const [decoded, setDecoded] = React.useState<Decoded>(null);
  const [gpc, setGpc] = React.useState<boolean | null>(null);
  const [tcf, setTcf] = React.useState<Record<string, unknown> | null>(null);
  const [gcm, setGcm] = React.useState<ReturnType<typeof runConsentModeChecks> | null>(null);
  const [from, setFrom] = React.useState<string>(() =>
    new Date(Date.now() - 7 * 86400000).toISOString().slice(0, 10),
  );
  const [to, setTo] = React.useState<string>(() => new Date().toISOString().slice(0, 10));
  // quick range handled by cards; keep from/to inputs only
  // export format/paging moved into ConsentsExportCard
  const [comparePrev] = React.useState<boolean>(false);
  const token = useGlobalStore((s: GlobalState) => s.token);
  // metrics are rendered in MetricsSnapshot
  // A/B demo (local)
  // A/B demo state managed within ABannerDemo component
  const [expName, setExpName] = React.useState<string>('cmp-banner-ab');
  const variants = React.useMemo(() => ['mb1', 'cc1', 'sp1', 'hb1', 'fs1', 'ts1'] as const, []);
  const baseUrl = String(
    (import.meta as unknown as { env?: Record<string, string | undefined> }).env?.[
      'VITE_CMP_REGISTRY_URL'
    ] || '',
  );
  const siteId = String(
    (import.meta as unknown as { env?: Record<string, string | undefined> }).env?.[
      'VITE_CMP_SITE_ID'
    ] || 'demo-site',
  );
  const [siteKey, setSiteKey] = React.useState<string>(siteId);
  const bannerExperiment = useExperimentVariant(expName, variants, baseUrl || undefined, siteKey);
  const assigned = bannerExperiment.variant as (typeof variants)[number];
  const [regionDecision, setRegionDecision] = React.useState<RegionCardDecision | null>(null);
  const [regionError, setRegionError] = React.useState<string | null>(null);
  const [regionLoading, setRegionLoading] = React.useState<boolean>(false);
  // assignment and event emission handled within ABannerDemo
  // Consent coverage (React Query)
  // Coverage/Scans card moved out; keep disabled queries to retain dependencies for URL state
  useQuery({
    queryKey: ['cmp', 'coverage-head', baseUrl, siteKey, from, to],
    queryFn: () => fetchConsentCoverage(baseUrl, siteKey, `${from}T00:00:00Z`, `${to}T23:59:59Z`),
    enabled: false,
  });

  // Previous period helpers (same window shifted back)
  const prevRange = React.useMemo(() => {
    try {
      const fromDate = new Date(`${from}T00:00:00Z`);
      const toDate = new Date(`${to}T23:59:59Z`);
      const spanMs = toDate.getTime() - fromDate.getTime() + 1;
      const prevTo = new Date(fromDate.getTime() - 1);
      const prevFrom = new Date(prevTo.getTime() - spanMs + 1);
      return { prevFromISO: prevFrom.toISOString(), prevToISO: prevTo.toISOString() };
    } catch {
      return { prevFromISO: undefined, prevToISO: undefined } as const;
    }
  }, [from, to]);

  const hasPrev = Boolean(comparePrev && prevRange.prevFromISO && prevRange.prevToISO);
  // keep a disabled prev query placeholder to preserve dependencies in effect calculation
  /* eslint-disable @typescript-eslint/no-unused-vars */
  const covPrevQ = useQuery({
    queryKey: [
      'cmp',
      'coverage',
      'prev',
      baseUrl,
      siteKey,
      prevRange.prevFromISO ?? null,
      prevRange.prevToISO ?? null,
    ],
    queryFn: () =>
      fetchConsentCoverage(
        baseUrl,
        siteKey,
        prevRange.prevFromISO as string,
        prevRange.prevToISO as string,
      ),
    enabled: false,
    staleTime: 60_000,
  });

  // Experiments (summary + series) — use the same global range as coverage/scans
  const toD = React.useMemo(() => new Date(`${to}T23:59:59Z`), [to]);
  const fromD = React.useMemo(() => new Date(`${from}T00:00:00Z`), [from]);
  const expSummaryQ = useQuery({
    queryKey: [
      'cmp',
      'experiments',
      'summary',
      baseUrl,
      siteKey,
      expName,
      fromD.toISOString(),
      toD.toISOString(),
    ],
    queryFn: () =>
      fetchExperimentsSummary(baseUrl, siteKey, expName, fromD.toISOString(), toD.toISOString()),
    enabled: Boolean(baseUrl),
    staleTime: 60_000,
  });
  const expSummaryPrevQ = useQuery({
    queryKey: [
      'cmp',
      'experiments',
      'summary',
      'prev',
      baseUrl,
      siteKey,
      expName,
      prevRange.prevFromISO ?? null,
      prevRange.prevToISO ?? null,
    ],
    queryFn: () =>
      fetchExperimentsSummary(
        baseUrl,
        siteKey,
        expName,
        (prevRange.prevFromISO as string) ?? '',
        (prevRange.prevToISO as string) ?? '',
      ),
    enabled: Boolean(baseUrl && hasPrev),
    staleTime: 60_000,
  });
  const expSeriesQ = useQuery({
    queryKey: [
      'cmp',
      'experiments',
      'series',
      baseUrl,
      siteKey,
      expName,
      assigned,
      fromD.toISOString(),
      toD.toISOString(),
    ],
    queryFn: () =>
      fetchExperimentsSeries(
        baseUrl,
        siteKey,
        expName,
        assigned,
        fromD.toISOString(),
        toD.toISOString(),
      ),
    enabled: Boolean(baseUrl),
    staleTime: 60_000,
  });
  useQuery({
    queryKey: ['cmp', 'scans-head', baseUrl, siteKey, from, to],
    queryFn: () => fetchScansDaily(baseUrl, siteKey, `${from}T00:00:00Z`, `${to}T23:59:59Z`),
    enabled: false,
  });
  // keep a disabled prev query placeholder for scans
  const scansPrevQ = useQuery({
    queryKey: [
      'cmp',
      'scans',
      'daily',
      'prev',
      baseUrl,
      siteKey,
      prevRange.prevFromISO ?? null,
      prevRange.prevToISO ?? null,
    ],
    queryFn: () =>
      fetchScansDaily(
        baseUrl,
        siteKey,
        prevRange.prevFromISO as string,
        prevRange.prevToISO as string,
      ),
    enabled: false,
    staleTime: 60_000,
  });
  /* eslint-enable @typescript-eslint/no-unused-vars */

  // URL state: restore on mount
  React.useEffect(() => {
    try {
      const q = new URLSearchParams(window.location.search);
      const qs = q.get('site');
      const qf = q.get('from');
      const qt = q.get('to');
      const qe = q.get('exp');
      const qv = q.get('variant');
      if (qs) setSiteKey(qs);
      if (qf && /^\d{4}-\d{2}-\d{2}$/.test(qf)) setFrom(qf);
      if (qt && /^\d{4}-\d{2}-\d{2}$/.test(qt)) setTo(qt);
      if (qe) setExpName(qe);
      if (qv && (variants as readonly string[]).includes(qv))
        bannerExperiment.setVariant(qv as (typeof variants)[number]);
    } catch {
      /* ignore */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // URL state: sync on change
  React.useEffect(() => {
    try {
      const url = new URL(window.location.href);
      url.searchParams.set('from', from);
      url.searchParams.set('to', to);
      url.searchParams.set('site', siteKey);
      url.searchParams.set('exp', expName);
      url.searchParams.set('variant', assigned);
      window.history.replaceState({}, '', url.toString());
    } catch {
      /* ignore */
    }
  }, [from, to, siteKey, expName, assigned]);

  React.useEffect(() => {
    const last = (window as unknown as { __lastGppString?: string }).__lastGppString;
    if (last) {
      setGpp(last);
      try {
        setDecoded(decodeGppString(last));
      } catch {
        setDecoded(null);
      }
    }
    const gpcFlag =
      (navigator as unknown as { globalPrivacyControl?: boolean }).globalPrivacyControl ?? null;
    setGpc(gpcFlag);
    // TCF diagnostics (flag-gated inside the SDK)
    try {
      installTcfCoreApiOnWindow(window as unknown as Window & typeof globalThis);
      const w = window as unknown as {
        __tcfapi?: (cmd: string, ver: number, cb: (data: unknown, ok: boolean) => void) => void;
      };
      if (typeof w.__tcfapi === 'function') {
        w.__tcfapi(
          'getTCData',
          2,
          (data, ok) => ok && setTcf((data as Record<string, unknown>) ?? null),
        );
        w.__tcfapi('addEventListener', 2, (data) =>
          setTcf((data as Record<string, unknown>) ?? null),
        );
      }
      const env =
        (import.meta as unknown as { env?: Record<string, string | undefined> }).env ?? {};
      if (env['VITE_TCF_ENABLE'] === '1' && env['VITE_TCF_GVL_URL']) {
        void loadPinnedGvl(String(env['VITE_TCF_GVL_URL']))
          .then(({ maxVendorId }) => setTcfVendorCap(maxVendorId))
          .catch(() => {
            /* ignore GVL fetch errors; keep default cap */
          });
      }
    } catch {
      /* noop */
    }
    try {
      const region = (window as unknown as { __cmpRegion?: string }).__cmpRegion ?? null;
      setGcm(runConsentModeChecks({ region }));
    } catch {
      /* noop */
    }
    // MetricsSnapshot card handles fetching/aggregation internally
  }, []);

  return (
    <div className="p-6 space-y-4">
      <h1 className="text-xl font-semibold">Diagnostics</h1>

      <GppTcfHeader gpc={gpc} gppString={gpp} decoded={decoded} tcf={tcf} />
      <GcmCard gcm={gcm} setGcm={setGcm} />
      <RegionDecisionCard
        baseUrl={baseUrl}
        onDecision={(d) => {
          setRegionDecision(d);
          setRegionError(null);
        }}
        onError={(message) => setRegionError(message)}
        onLoadingChange={setRegionLoading}
      />
      <FirstRunChecklist
        siteKey={siteKey}
        regionDecision={regionDecision}
        regionError={regionError}
        regionLoading={regionLoading}
      />

      <ABannerDemo
        siteKey={siteKey}
        from={from}
        to={to}
        expName={expName}
        setExpName={setExpName}
        assigned={assigned}
        setAssigned={(variant) => bannerExperiment.setVariant(variant as (typeof variants)[number])}
        emitEvent={bannerExperiment.emit}
        variants={variants}
        onRefresh={() => {
          void expSummaryQ.refetch();
          void expSeriesQ.refetch();
        }}
      />

      <CoverageCard
        baseUrl={baseUrl}
        siteKey={siteKey}
        from={from}
        to={to}
        comparePrev={comparePrev}
      />
      <div id="scans-card">
        <ScansCard
          baseUrl={baseUrl}
          siteKey={siteKey}
          from={from}
          to={to}
          comparePrev={comparePrev}
        />
      </div>

      <ConsentsExportCard
        baseUrl={baseUrl}
        siteKey={siteId}
        from={from}
        to={to}
        bearer={token || undefined}
      />
      <ReceiptsExportCard
        baseUrl={baseUrl}
        siteKey={siteId}
        from={from}
        to={to}
        bearer={token || undefined}
      />

      <div className="rounded-lg border p-4 text-sm">
        <h2 className="text-lg font-semibold mb-2">Receipts (admin)</h2>
        <ReceiptsTable baseUrl={baseUrl} bearer={token || undefined} />
      </div>

      <MetricsSnapshot baseUrl={baseUrl} />
    </div>
  );
}
